/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  Component,
  OnInit,
  ViewChild
} from '@angular/core';

import { Logger                } from '@/util/logger';
import { UserService }           from '@/services/user.service';
import { MatSidenav }            from '@angular/material/sidenav';
import { IUserRow }              from '@/screens/administration/i.user.row';
import {first}                   from "rxjs/operators";
import {bytesToSize}             from "@/util/util";

import { PrimeIcons }            from "primeng/api";
import { SortEvent }             from 'primeng/api';

@Component({
  templateUrl: 'user.list.page.component.html',
})

/**
 *
 * @class UserListPageComponent
 *
 * @brief user management (user directory page)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class UserListPageComponent implements OnInit {

  @ViewChild('adminsidenav')
  public         nav: MatSidenav;

  /** the list of CSV style entries for each usser in the system */
  public userList: IUserRow[] = [];

  /** standard constructor */
  constructor(
    private users: UserService
  ) {

  }

  /** open or close the side nav panel */
  toggleSidebar() {

    this.nav.toggle();
  }

  /** on page setup, create the form validator */
  ngOnInit() {

    Logger.debug('[UserListPage] init...');

    /* fetch the user dump... */

    let ul: UserListPageComponent = this;

    ul.users.dump()
      .pipe(first())
      .subscribe(
        (rows: IUserRow[]) => {

          Logger.debug(`[UserListPage] users: `, rows);

          ul.userList = rows;

        },
        error => {

          Logger.error(`[UserListPage] could not fetch user report: `, error);

        });
  }

  /** helper to sort special rows like disk usage */
  tableSort(event: SortEvent) {

    event.data.sort((data1, data2) => {

      let value1 = data1[event.field];
      let value2 = data2[event.field];
      let result = null;

      /* deal with missing data */

      if (value1 == null && value2 != null) {

        return -1;

      } else if (value1 != null && value2 == null) {

        return 1;

      } else if (value1 == null && value2 == null) {

        return 0;
      }

      if (['id', 'files'].includes(event.field)) {

        /* straight number compare */

        return (parseFloat(value2) - parseFloat(value1)) * event.order;
      }

      if (['userid', 'display_name', 'email'].includes(event.field)) {

        /* straight string compare */

        return (value1.toString().localeCompare(value2.toString())) * event.order;
      }

      if (event.field == 'created_at') {

        /* date compare */

        const t1 = value1.split(/[- :]/);
        const d1 = new Date(Date.UTC(t1[0], t1[1]-1, t1[2], t1[3], t1[4], t1[5]));

        const t2 = value2.split(/[- :]/);
        const d2 = new Date(Date.UTC(t2[0], t2[1]-1, t2[2], t2[3], t2[4], t2[5]));

        return (d2.getTime() - d1.getTime()) * event.order;
      }

      if (event.field == 'usage') {

        let v1 = parseFloat(value1);
        let v2 = parseFloat(value2);

        if (!isNaN(v1) && isNaN(v2)) {

          return -1;

        } else if (isNaN(v1) && !isNaN(v2)) {

          return 1;

        } else if (isNaN(v1) && isNaN(v2)) {

          return 0;
        }

        /* pretty printed bytes compare */

        if (/kib|k|kb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024;
        }

        if (/mib|m|mb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024 * 1024;
        }

        if (/gib|g|gb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024 * 1024 * 1024;
        }

        if (/tib|t|tb$/.test(value1.toString().toLowerCase())) {

          v1 *= 1024 * 1024 * 1024 * 1024;
        }


        if (/kib|k|kb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024;
        }

        if (/mib|m|mb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024 * 1024;
        }

        if (/gib|g|gb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024 * 1024 * 1024;
        }

        if (/tib|t|tb$/.test(value2.toString().toLowerCase())) {

          v2 *= 1024 * 1024 * 1024 * 1024;
        }

        return (v2 - v1) * event.order;
      }

      /* can not interpret */

      return 0;
    });
  }

  /** helper to update this item in the user list model */
  updateUser(data: IUserRow) {

    this.userList = this.userList.map((item: IUserRow) => {

      return item.userid == data.userid ? data : item;
    });

  }

  /** grant admin rights */
  promoteAdmin(userid: string) {

    Logger.debug(`[UserListPage] promoting user: `, userid);

    let ul: UserListPageComponent = this;

    ul.users.promoteUser(userid)
      .pipe(first())
      .subscribe(
        (edited: IUserRow) => {

          Logger.debug(`[UserListPage] (promote) updated: `, edited);

          ul.updateUser(edited);

        },
        error => {

          Logger.error(`[UserListPage] (promote) error: `, error);

        });
  }

  /** take away admin rights */
  demoteAdmin(userid: string) {

    Logger.debug(`[UserListPage] demoting user: `, userid);

    let ul: UserListPageComponent = this;

    ul.users.demote(userid)
      .pipe(first())
      .subscribe(
        (edited: IUserRow) => {

          Logger.debug(`[UserListPage] (demote) updated: `, edited);

          ul.updateUser(edited);

        },
        error => {

          Logger.error(`[UserListPage] (demote) error: `, error);

        });
  }

  /** lock/suspend a user */
  unlockUser(userid: string) {

    Logger.debug(`[UserListPage] unlocking user: `, userid);

    let ul: UserListPageComponent = this;

    ul.users.unlock(userid)
      .pipe(first())
      .subscribe(
        (edited: IUserRow) => {

          Logger.debug(`[UserListPage] (unlock) updated: `, edited);

          ul.updateUser(edited);

        },
        error => {

          Logger.error(`[UserListPage] (unlock) error: `, error);

        });
  }

  /** unlock/resume a user */
  lockUser(userid: string) {

    Logger.debug(`[UserListPage] locking user: `, userid);

    let ul: UserListPageComponent = this;

    ul.users.lock(userid)
      .pipe(first())
      .subscribe(
        (edited: IUserRow) => {

          Logger.debug(`[UserListPage] (lock) updated: `, edited);

          ul.updateUser(edited);

        },
        error => {

          Logger.error(`[UserListPage] (lock) error: `, error);

        });
  }

  /** delete a user */
  deleteUser(userid: string) {

    Logger.debug(`[UserListPage] deleting user: `, userid);

    let ul: UserListPageComponent = this;

    ul.users.deleteUser(userid)
      .pipe(first())
      .subscribe(
        (edited: IUserRow) => {

          Logger.debug(`[UserListPage] (delete) updated: `, edited);

          ul.updateUser(edited);

        },
        error => {

          Logger.error(`[UserListPage] (delete) error: `, error);

        });
  }

  /** un-delete a user */
  unDeleteUser(userid: string) {

    Logger.debug(`[UserListPage] un-deleting user: `, userid);

    let ul: UserListPageComponent = this;

    ul.users.undeleteUser(userid)
      .pipe(first())
      .subscribe(
        (edited: IUserRow) => {

          Logger.debug(`[UserListPage] (un-delete) updated: `, edited);

          ul.updateUser(edited);

        },
        error => {

          Logger.error(`[UserListPage] (un-delete) error: `, error);

        });
  }
}
